import React, { useState, useEffect } from 'react';
import EmployeeForm from './EmployeeForm';
import { getDepartments, getEmployee, updateEmployee, bulkUploadEmployeeDocuments, deleteEmployeeDocument, updateEmployeeDocument, imagepath } from '../api';
import { message, Spin, Alert } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';

const EmployeeEdit = () => {
  const { id } = useParams();
  const [departments, setDepartments] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const safeDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith('0000-00-00')) return '';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getDepartments(),
      getEmployee(id)
    ]).then(([deptRes, empRes]) => {
      if (deptRes.success) setDepartments(deptRes.data);
      if (empRes.success) {
        const emp = { ...empRes.data };
        if (emp.profile_image) {
          emp.profile_image = [
            {
              uid: '-1',
              name: emp.profile_image.split('/').pop(),
              status: 'done',
              url: `${imagepath}${emp.profile_image}`,
            }
          ];
        } else {
          emp.profile_image = [];
        }
        if (emp.skills && Array.isArray(emp.skills)) {
          emp.skills = emp.skills.map(s => s.id);
        }
        if (emp.leaves && Array.isArray(emp.leaves)) {
          emp.leaves = emp.leaves.map(l => ({
            ...l,
            start_date: safeDate(l.start_date),
            end_date: safeDate(l.end_date),
            actual_return_date: safeDate(l.actual_return_date),
          }));
        }
        if (emp.documents && Array.isArray(emp.documents)) {
          emp.documents = emp.documents.map(doc => {
            if (!doc.document_file) return { ...doc, document_file: [] };
            if (Array.isArray(doc.document_file)) return doc;
            const rawPath = typeof doc.document_file === 'string' ? doc.document_file : '';
            const fileUrl = rawPath.startsWith('http') ? rawPath : `${imagepath}${rawPath}`;
            const fileName = doc.original_filename || (rawPath ? rawPath.split('/').pop() : 'document');
            return {
              ...doc,
              document_file: [{
                uid: String(doc.id || Math.random()),
                name: fileName,
                status: 'done',
                url: fileUrl,
                thumbUrl: fileUrl,
              }]
            };
          });
        }
        emp.dob = safeDate(emp.dob);
        emp.joining_date = safeDate(emp.joining_date);
        emp.workmen_insurance_issue_date = safeDate(emp.workmen_insurance_issue_date);
        emp.workmen_insurance_expiry_date = safeDate(emp.workmen_insurance_expiry_date);
        setInitialValues(emp);
      } else {
        setError(empRes.message || 'Employee not found');
      }
      setLoading(false);
    }).catch(() => {
      setError('Failed to load data');
      setLoading(false);
    });
  }, [id]);

  const handleSubmit = async (values) => {
    console.log('EmployeeEdit handleSubmit - Received values:', values);
    console.log('Documents in values:', values.documents);
    setLoading(true);
    try {
      // 1. Find documents that need to be deleted (completely removed from the UI list)
      const removedDocIds = (initialValues.documents || [])
        .filter(initDoc => !values.documents?.some(d => d.id === initDoc.id))
        .map(doc => doc.id);

      // 2. Delete these documents from backend
      if (removedDocIds.length > 0) {
        await Promise.all(
          removedDocIds.map(docId => deleteEmployeeDocument(id, docId))
        );
      }
      // 3. Prepare formData for employee update
      const formData = new FormData();
      const fieldsToAppend = [
        'name', 'email', 'status', 'emp_code', 'phone', 'native_phone', 'dob', 'address', 'country',
        'department_id', 'joining_date', 'base_salary', 'over_time_rate',
        'hourly_rate', 'salary_type', 'workmen_insurance_issue_date', 'workmen_insurance_expiry_date'
      ];

      fieldsToAppend.forEach(field => {
        const value = values[field];
        if (value !== undefined && value !== null && value !== '') {
          formData.append(field, value);
        }
      });

      if (values.profile_image && values.profile_image.length > 0) {
        const fileObj = values.profile_image[0];
        if (fileObj.originFileObj) {
          formData.append('profile_image', fileObj.originFileObj);
        }
      }
      formData.append('update_skills', '1');
      if (Array.isArray(values.skills)) {
        values.skills.forEach(skillId => {
          formData.append('skills[]', skillId);
        });
      }
      formData.append('update_leaves', '1');
      if (values.leaves && Array.isArray(values.leaves)) {
        values.leaves.forEach((leave, idx) => {
          formData.append(`leaves[${idx}][leave_type]`, leave.leave_type || '');
          formData.append(`leaves[${idx}][start_date]`, leave.start_date || '');
          formData.append(`leaves[${idx}][end_date]`, leave.end_date || '');
          formData.append(`leaves[${idx}][actual_return_date]`, leave.actual_return_date || '');
          formData.append(`leaves[${idx}][reason]`, leave.reason || '');
          if (leave.form_file && leave.form_file.length > 0) {
            const fileObj = leave.form_file[0];
            if (fileObj.originFileObj) {
              formData.append(`leaves[${idx}][form_file]`, fileObj.originFileObj);
            } else if (leave.leave_application_form) {
              formData.append(`leaves[${idx}][leave_application_form]`, leave.leave_application_form);
            }
          }
        });
      }
      // 4. Update employee details
      const res = await updateEmployee(id, formData);
      if (!res.success) {
        throw new Error(res.message || 'Failed to update employee');
      }
      // 5. Handle document uploads (new/updated only)
      if (values.documents && values.documents.length > 0) {
        // 5a. Upload new documents (no ID exists)
        const documentsToUpload = values.documents
          .filter(doc => !doc.id && doc.document_file?.[0]?.originFileObj)
          .map(doc => ({
            document_type: doc.document_type,
            issue_date: doc.issue_date,
            expiry_date: doc.expiry_date,
            document_file: doc.document_file[0].originFileObj
          }));
        if (documentsToUpload.length > 0) {
          const docRes = await bulkUploadEmployeeDocuments(id, documentsToUpload);
          if (!docRes.success) {
            throw new Error(docRes.message || 'Failed to upload documents');
          }
        }

        // 5b. Update existing documents (have an ID) in place
        const documentsToUpdate = values.documents.filter(doc => !!doc.id);
        if (documentsToUpdate.length > 0) {
          const updatePromises = documentsToUpdate.map(doc => {
            const fileObj = doc.document_file?.[0]?.originFileObj;
            return updateEmployeeDocument(id, doc.id, {
              document_type: doc.document_type,
              issue_date: doc.issue_date,
              expiry_date: doc.expiry_date,
              document_file: fileObj || null // Only send file if a new file is uploaded
            });
          });
          const updateResults = await Promise.all(updatePromises);
          const failedUpdate = updateResults.find(res => !res.success);
          if (failedUpdate) {
            throw new Error(failedUpdate.message || 'Failed to update some document details');
          }
        }
      }
      message.success('Employee updated successfully');
      navigate('/employees');
    } catch (error) {
      console.error('Update error:', error);
      message.error(error.message || 'Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 100 }}><Spin size="large" /></div>;
  if (error) return <Alert type="error" message={error} style={{ margin: 40 }} />;
  if (!initialValues) return null;

  return (
    <EmployeeForm
      key={initialValues.id}
      mode="edit"
      initialValues={initialValues}
      departments={departments}
      onSubmit={handleSubmit}
      loading={loading}
      onCancel={() => navigate('/employees')}
    />
  );
};

export default EmployeeEdit; 